import { useCallback, useState } from "react";

export const ADMIN_PAGE_SIZE = 10;

/** Page state + skip/limit for chunked admin list APIs. */
export function useAdminPager(pageSize = ADMIN_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const skip = (page - 1) * pageSize;
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize) || 1);

  const goToPage = useCallback(
    (next) => {
      const n = Number(next);
      if (!Number.isFinite(n) || n < 1) return;
      setPage(n);
    },
    [],
  );

  const resetPage = useCallback(() => setPage(1), []);

  return {
    page,
    setPage: goToPage,
    resetPage,
    skip,
    limit: pageSize,
    pageSize,
    total,
    setTotal,
    totalPages,
  };
}

const LIST_KEYS = [
  "items",
  "users",
  "admins",
  "posts",
  "tickets",
  "announcements",
  "logs",
  "transactions",
  "deposits",
  "withdrawals",
  "whitelist",
  "deposit_whitelist",
  "disputes",
  "sessions",
  "orders",
  "positions",
  "alerts",
  "cases",
  "trades",
];

/** Always return an array — paged APIs send {items,total}, not a bare list. */
export function asAdminList(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  if (Array.isArray(value.items)) return value.items;
  for (const k of LIST_KEYS) {
    if (Array.isArray(value[k])) return value[k];
  }
  return [];
}

/** Normalize list API payloads that may be an array or {items,total}. */
export function unwrapAdminList(data, itemKey) {
  if (Array.isArray(data)) {
    return { items: data, total: data.length };
  }
  if (!data || typeof data !== "object") {
    return { items: [], total: 0 };
  }
  const items = (itemKey && Array.isArray(data[itemKey]) ? data[itemKey] : null) || asAdminList(data);
  return {
    items,
    total: Number(data.total ?? items.length) || 0,
    extra: data,
  };
}
